<xemex-dialog-shell [title]="'installations.editor.history.title' | translate: { point: name }">
  <div class="history">
    @if (isLoading()) {
      <xemex-spinner />
    } @else if (loadFailed()) {
      <div class="flex items-center justify-between gap-4">
        <span>{{ 'installations.editor.history.failed' | translate }}</span>
        <xemex-text-button icon="refresh" (click)="load()">
          {{ 'installations.insights.retry' | translate }}
        </xemex-text-button>
      </div>
    } @else if (detail()) {
      @if (periods().length === 0) {
        <p class="m-0 text-text-secondary">
          {{ 'installations.editor.history.empty' | translate }}
        </p>
      }
      <ol class="history__periods">
        @for (period of periods(); track period.binding.bindingOid) {
          <li class="history__period" [class.history__period--retracted]="period.binding.retracted">
            <div class="history__head">
              <span class="history__interval">
                {{ period.from }} –
                {{ period.to ?? ('installations.editor.history.open' | translate) }}
              </span>
              @if (period.binding.retracted) {
                <span class="history__note history__note--warning">
                  {{ 'installations.editor.history.retracted' | translate }}
                </span>
              }
              @if (period.binding.offCatalogue) {
                <span class="history__note">
                  {{ 'installations.editor.history.offCatalogue' | translate }}
                </span>
              }
            </div>
            <dl class="history__facts">
              <dt>{{ 'installations.points.source' | translate }}</dt>
              <dd>
                {{ period.source }}
                @if (period.binding.source.label) {
                  ({{ period.binding.source.label }})
                }
              </dd>
              <dt>{{ 'installations.editor.history.provenance' | translate }}</dt>
              <dd>{{ period.binding.provenance }}</dd>
              @if (period.closeReason; as closeReason) {
                <dt>{{ 'installations.editor.history.closeReason' | translate }}</dt>
                <dd>{{ closeReason }}</dd>
              }
              @if (period.binding.retracted; as retraction) {
                <dt>{{ 'installations.editor.history.retractedAt' | translate }}</dt>
                <dd>{{ period.retractedAt }}</dd>
                <dt>{{ 'installations.editor.retract.reason' | translate }}</dt>
                <dd>{{ retraction.reason }}</dd>
                @if (retraction.supersededByBindingOid) {
                  <dt>{{ 'installations.editor.history.correction' | translate }}</dt>
                  <dd>{{ 'installations.editor.history.corrected' | translate }}</dd>
                }
              }
            </dl>

            @if (data.canRetract && !period.binding.retracted) {
              @if (retractingOid() === period.binding.bindingOid) {
                <div class="history__retract">
                  <p class="m-0">{{ 'installations.editor.retract.lead' | translate }}</p>
                  <xemex-text-field
                    [label]="'installations.editor.retract.reason' | translate"
                    [required]="true"
                    [invalid]="reasonTouched() && reasonInvalid()"
                    [errorText]="
                      'installations.editor.retract.reasonInvalid'
                        | translate: { max: reasonMaxLength }
                    "
                    [ngModel]="reason()"
                    (ngModelChange)="reason.set($event)"
                  />

                  <span class="history__label">
                    {{ 'installations.editor.retract.correction' | translate }}
                  </span>
                  @if (isLoadingCandidates()) {
                    <xemex-spinner size="sm" />
                  } @else if (candidatesFailed()) {
                    <div class="flex items-center justify-between gap-4">
                      <span>{{ 'installations.editor.binding.candidatesFailed' | translate }}</span>
                      <xemex-text-button icon="refresh" (click)="loadCandidates()">
                        {{ 'installations.insights.retry' | translate }}
                      </xemex-text-button>
                    </div>
                  } @else {
                    @if (messageAt('candidates'); as messageKey) {
                      <p class="m-0 text-text-error" role="alert">{{ messageKey | translate }}</p>
                    }
                    <mat-radio-group
                      class="history__corrections"
                      [attr.aria-label]="'installations.editor.retract.correction' | translate"
                      [value]="correctionKey()"
                      (change)="correctionKey.set($event.value)"
                    >
                      <mat-radio-button class="history__correction" [value]="noCorrection">
                        {{ 'installations.editor.retract.noCorrection' | translate }}
                      </mat-radio-button>
                      @for (row of corrections(); track row.key) {
                        <mat-radio-button class="history__correction" [value]="row.key">
                          <xemex-channel-candidate
                            [candidate]="row.candidate"
                            [current]="row.current"
                            [sharedWith]="row.sharedWith"
                            [error]="candidateError(row.key)"
                          />
                        </mat-radio-button>
                      }
                    </mat-radio-group>
                  }

                  @if (messageAt('action'); as messageKey) {
                    <p class="m-0 text-text-error" role="alert">{{ messageKey | translate }}</p>
                  }
                  <div class="flex items-center justify-end gap-3">
                    <xemex-text-button (click)="stopRetract()">
                      {{ 'general.btnCancel' | translate }}
                    </xemex-text-button>
                    <xemex-filled-button
                      intent="destructive"
                      [disabled]="isSaving() || stale()"
                      [loading]="isSaving()"
                      (click)="retract()"
                    >
                      {{ 'installations.editor.retract.confirm' | translate }}
                    </xemex-filled-button>
                  </div>
                </div>
              } @else if (retractingOid() === null) {
                <div>
                  <xemex-text-button
                    intent="destructive"
                    icon="undo"
                    (click)="startRetract(period.binding.bindingOid)"
                  >
                    {{ 'installations.editor.retract.action' | translate }}
                  </xemex-text-button>
                </div>
              }
            }
          </li>
        }
      </ol>
    }

    @if (messageAt('dialog'); as messageKey) {
      <p class="m-0 text-text-error" role="alert">{{ messageKey | translate }}</p>
    }
  </div>

  <div actions class="flex w-full items-center justify-end">
    <xemex-text-button (click)="close()">
      {{ 'general.btnClose' | translate }}
    </xemex-text-button>
  </div>
</xemex-dialog-shell>
